// YERİ: src/app/domains/site/sections/sportsclub.sections.ts
//
// SPOR KULÜBÜ BÖLÜMLERİ — fikstür, puan durumu, saha planı.
//
// ── NEDEN AYARDAN DEĞİL SUNUCUDAN ─────────────────────────────────────────
// Bu üçü kulübün portalda ZATEN tuttuğu kayıtlar. Bölüm ayarına elle
// yazdırılsaydı kulüp her maçtan sonra aynı veriyi İKİ yerde güncellemek
// zorunda kalırdı ve site kaçınılmaz olarak eskirdi. Bölüm yalnız "neyi
// göstereyim" (kapsam, adet, başlık) sorusunu ayardan okur.
//
// ── VERİ YOKSA BÖLÜM BASILMAZ ─────────────────────────────────────────────
// Örnek maç / uydurma takım ADI GÖSTERİLMEZ. Blog bölümünde tam olarak bu
// hata vardı: liste boşken "Yazı Başlığı 1" basılıyor, ziyaretçi var olmayan
// içeriğe tıklıyordu. Fikstürde karşılığı "Takım A - Takım B" olurdu.

import { Component, OnInit, computed, inject, input, signal } from '@angular/core';
import { SiteConfig, cfg } from './section.model';
import { SiteChromeText } from '../core/site-chrome-text';
import {
  TenantWebService, TenantFixture, TenantFormation, TenantStandings,
} from '../core/tenant-web.service';

/**
 * Sunucudan gelen tarih ZAMAN DİLİMSİZ (naive) bir duvar saatidir: kulüp
 * "19:00" girdiyse ziyaretçi de 19:00 görmeli — ziyaretçinin tarayıcı
 * saat dilimine göre kaydırmak maç saatini yanlış gösterir.
 *
 * Bu yüzden değer UTC'ymiş gibi ayrıştırılıp `timeZone:'UTC'` ile basılıyor:
 * sonuç, hangi ülkeden bakılırsa bakılsın girilen saatin aynısı.
 */
function duvarSaati(iso: string | null | undefined, lang: string, saatliMi: boolean): string {
  if (!iso) return '';

  // Sondaki 'Z' veya '+03:00' gibi ekler atılır; kalan kısım UTC sayılır.
  const temiz = iso.replace(/(Z|[+-]\d{2}:?\d{2})$/i, '');
  const d = new Date(temiz + 'Z');
  if (Number.isNaN(d.getTime())) return '';

  const option: Intl.DateTimeFormatOptions = saatliMi
    ? { day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit', timeZone: 'UTC' }
    : { day: '2-digit', month: 'short', year: 'numeric', timeZone: 'UTC' };

  try {
    return new Intl.DateTimeFormat(lang, option).format(d);
  } catch {
    // Geçersiz dil kodu Intl'i patlatır; site bir tarih yüzünden çökmemeli.
    return new Intl.DateTimeFormat('en', option).format(d);
  }
}

// ══════════════════════════════════════════════════════════════════════════
// FİKSTÜR / SONUÇLAR
// ══════════════════════════════════════════════════════════════════════════

@Component({
  selector: 'sec-fixture-list',
  standalone: true,
  template: `
    @if (mac().length) {
    <section id="fixture" class="bg-white py-20 dark:bg-neutral-950">
      <div class="mx-auto max-w-5xl px-6 lg:px-8">
        <div class="text-center">
          @if (eyebrow()) {
            <p class="text-sm font-semibold uppercase tracking-widest" style="color:var(--site-accent)">{{ eyebrow() }}</p>
          }
          <h2 class="mt-3 font-[family-name:var(--site-head-font)] text-3xl font-bold tracking-tight text-neutral-900 dark:text-white sm:text-4xl">{{ title() }}</h2>
          @if (subtitle()) {
            <p class="mx-auto mt-4 max-w-2xl text-lg text-neutral-600 dark:text-neutral-400">{{ subtitle() }}</p>
          }
        </div>

        <ul class="mt-10 space-y-3">
          @for (m of mac(); track m.id) {
            <li class="rounded-2xl border border-neutral-200 bg-white p-4 transition hover:shadow-md dark:border-neutral-800 dark:bg-neutral-900 sm:p-5">
              <!-- Üst satır: turnuva + hafta + tarih -->
              <div class="flex flex-wrap items-center justify-between gap-2 text-xs text-neutral-500 dark:text-neutral-400">
                <span class="font-medium uppercase tracking-wide">
                  {{ m.competitionName }}
                  @if (m.round > 0) { <span class="ml-1">· {{ text.t('sp.round') }} {{ m.round }}</span> }
                </span>
                @if (m.scheduledAt) { <span>{{ date(m) }}</span> }
              </div>

              <!-- Eşleşme: ev sahibi — skor/vs — deplasman -->
              <div class="mt-3 grid grid-cols-[1fr_auto_1fr] items-center gap-3">
                <span class="text-right text-base font-semibold text-neutral-900 dark:text-white sm:text-lg">{{ m.homeTeamName }}</span>

                @if (skorVar(m)) {
                  <span class="rounded-lg px-3 py-1 text-base font-bold tabular-nums text-white sm:text-lg"
                        style="background:var(--site-primary)">{{ m.scoreHome }} - {{ m.scoreAway }}</span>
                } @else {
                  <span class="rounded-lg border border-neutral-200 px-3 py-1 text-xs font-semibold uppercase tracking-wide text-neutral-500 dark:border-neutral-700 dark:text-neutral-400">{{ text.t('sp.vs') }}</span>
                }

                <span class="text-left text-base font-semibold text-neutral-900 dark:text-white sm:text-lg">{{ m.awayTeamName }}</span>
              </div>

              <!-- Alt satır: saha + ertelendi rozeti -->
              @if (m.venue || ertelendi(m)) {
                <div class="mt-3 flex flex-wrap items-center justify-center gap-2 text-xs text-neutral-500 dark:text-neutral-400">
                  @if (m.venue) { <span>{{ text.t('sp.venue') }}: {{ m.venue }}</span> }
                  @if (ertelendi(m)) {
                    <span class="rounded-full bg-amber-100 px-2 py-0.5 font-semibold text-amber-800 dark:bg-amber-900/40 dark:text-amber-300">{{ text.t('sp.postponed') }}</span>
                  }
                </div>
              }
            </li>
          }
        </ul>
      </div>
    </section>
    }
  `,
})
export class FixtureListSection implements OnInit {
  protected text = inject(SiteChromeText);
  private svc = inject(TenantWebService);

  config = input<SiteConfig>({});
  clientId = input<number>(0);

  protected mac = signal<TenantFixture[]>([]);

  ngOnInit() {
    const id = this.clientId();
    if (!id) return;

    const scope = String(cfg(this.config(), 'scope', 'upcoming')) as 'upcoming' | 'results' | 'all';
    const qty = Number(cfg(this.config(), 'count', 5)) || 5;

    this.svc.getSportsFixtures(id, scope, qty).subscribe({
      next: (l) => this.mac.set(l),
      // Uç ulaşılamazsa bölüm sessizce boş kalır; ziyaretçiye hata basmak yerine
      // bölümü hiç göstermemek doğru.
      error: () => this.mac.set([]),
    });
  }

  protected eyebrow = computed(() => cfg(this.config(), 'eyebrow', ''));
  protected title = computed(() => cfg(this.config(), 'title', this.text.t('fb.fikstur')));
  protected subtitle = computed(() => cfg(this.config(), 'subtitle', ''));

  /**
   * Saat gösterilsin mi — kulüp yalnız günü duyurmak isteyebilir.
   *
   * Değer select'ten geldiği için METİN ('true'/'false'); ham boolean olarak
   * karşılaştırılsaydı 'false' dizesi doğru sayılır ve ayar hiç işlemezdi.
   */
  private saatli = computed(() => String(cfg(this.config(), 'showTime', 'true')) !== 'false');

  protected date = (m: TenantFixture) => duvarSaati(m.scheduledAt, this.text.lang(), this.saatli());
  protected skorVar = (m: TenantFixture) => m.scoreHome != null && m.scoreAway != null;
  protected ertelendi = (m: TenantFixture) => (m.status || '').toLowerCase() === 'postponed';
}

// ══════════════════════════════════════════════════════════════════════════
// PUAN DURUMU
// ══════════════════════════════════════════════════════════════════════════

@Component({
  selector: 'sec-standings-table',
  standalone: true,
  template: `
    @if (row().length) {
    <section id="standings" class="bg-neutral-50 py-20 dark:bg-neutral-900">
      <div class="mx-auto max-w-4xl px-6 lg:px-8">
        <div class="text-center">
          @if (eyebrow()) {
            <p class="text-sm font-semibold uppercase tracking-widest" style="color:var(--site-accent)">{{ eyebrow() }}</p>
          }
          <h2 class="mt-3 font-[family-name:var(--site-head-font)] text-3xl font-bold tracking-tight text-neutral-900 dark:text-white sm:text-4xl">{{ title() }}</h2>
          <p class="mt-3 text-sm text-neutral-500 dark:text-neutral-400">
            {{ tablo()?.competitionName }}
            @if (tablo()?.season) { <span>· {{ tablo()?.season }}</span> }
          </p>
        </div>

        <!-- Dar ekranda tablo kendi içinde kayar; sayfa yatay kaymaz. -->
        <div class="mt-8 overflow-x-auto rounded-2xl border border-neutral-200 bg-white dark:border-neutral-800 dark:bg-neutral-950">
          <table class="w-full min-w-[34rem] text-sm">
            <thead>
              <tr class="border-b border-neutral-200 text-xs uppercase tracking-wide text-neutral-500 dark:border-neutral-800 dark:text-neutral-400">
                <th class="px-3 py-3 text-left font-semibold">#</th>
                <th class="px-3 py-3 text-left font-semibold">{{ text.t('sp.team') }}</th>
                <th class="px-2 py-3 text-center font-semibold" [title]="text.t('sp.playedLong')">{{ text.t('sp.played') }}</th>
                <th class="px-2 py-3 text-center font-semibold" [title]="text.t('sp.wonLong')">{{ text.t('sp.won') }}</th>
                <th class="px-2 py-3 text-center font-semibold" [title]="text.t('sp.drawnLong')">{{ text.t('sp.drawn') }}</th>
                <th class="px-2 py-3 text-center font-semibold" [title]="text.t('sp.lostLong')">{{ text.t('sp.lost') }}</th>
                <th class="px-2 py-3 text-center font-semibold" [title]="text.t('sp.goalsLong')">{{ text.t('sp.goals') }}</th>
                <th class="px-2 py-3 text-center font-semibold" [title]="text.t('sp.diffLong')">{{ text.t('sp.diff') }}</th>
                <th class="px-3 py-3 text-center font-bold" [title]="text.t('sp.pointsLong')">{{ text.t('sp.points') }}</th>
              </tr>
            </thead>
            <tbody>
              @for (r of row(); track r.teamName) {
                <tr class="border-b border-neutral-100 last:border-0 dark:border-neutral-900">
                  <td class="px-3 py-3 text-left tabular-nums text-neutral-500 dark:text-neutral-400">{{ r.rank }}</td>
                  <td class="px-3 py-3 text-left font-semibold text-neutral-900 dark:text-white">{{ r.teamName }}</td>
                  <td class="px-2 py-3 text-center tabular-nums text-neutral-600 dark:text-neutral-400">{{ r.played }}</td>
                  <td class="px-2 py-3 text-center tabular-nums text-neutral-600 dark:text-neutral-400">{{ r.won }}</td>
                  <td class="px-2 py-3 text-center tabular-nums text-neutral-600 dark:text-neutral-400">{{ r.drawn }}</td>
                  <td class="px-2 py-3 text-center tabular-nums text-neutral-600 dark:text-neutral-400">{{ r.lost }}</td>
                  <td class="px-2 py-3 text-center tabular-nums text-neutral-600 dark:text-neutral-400">{{ r.goalsFor }}-{{ r.goalsAgainst }}</td>
                  <td class="px-2 py-3 text-center tabular-nums text-neutral-600 dark:text-neutral-400">{{ r.goalDifference > 0 ? '+' : '' }}{{ r.goalDifference }}</td>
                  <td class="px-3 py-3 text-center font-bold tabular-nums" style="color:var(--site-primary)">{{ r.points }}</td>
                </tr>
              }
            </tbody>
          </table>
        </div>
      </div>
    </section>
    }
  `,
})
export class StandingsTableSection implements OnInit {
  protected text = inject(SiteChromeText);
  private svc = inject(TenantWebService);

  config = input<SiteConfig>({});
  clientId = input<number>(0);

  protected tablo = signal<TenantStandings | null>(null);
  protected row = computed(() => this.tablo()?.rows ?? []);

  ngOnInit() {
    const id = this.clientId();
    if (!id) return;

    // Turnuva seçilmediyse sunucu en güncel olanı seçer.
    const ham = cfg(this.config(), 'competitionId', '');
    const turnuva = Number(ham) > 0 ? Number(ham) : null;

    this.svc.getSportsStandings(id, turnuva).subscribe({
      next: (t) => this.tablo.set(t),
      error: () => this.tablo.set(null),
    });
  }

  protected eyebrow = computed(() => cfg(this.config(), 'eyebrow', ''));
  protected title = computed(() => cfg(this.config(), 'title', this.text.t('sd.puanDurumu')));
}

// ══════════════════════════════════════════════════════════════════════════
// SAHA PLANI (DİZİLİŞ)
// ══════════════════════════════════════════════════════════════════════════

@Component({
  selector: 'sec-formation-pitch',
  standalone: true,
  template: `
    @if (nokta().length) {
    <section id="formation" class="bg-white py-20 dark:bg-neutral-950">
      <div class="mx-auto max-w-4xl px-6 lg:px-8">
        <div class="text-center">
          @if (eyebrow()) {
            <p class="text-sm font-semibold uppercase tracking-widest" style="color:var(--site-accent)">{{ eyebrow() }}</p>
          }
          <h2 class="mt-3 font-[family-name:var(--site-head-font)] text-3xl font-bold tracking-tight text-neutral-900 dark:text-white sm:text-4xl">{{ title() }}</h2>
          <p class="mt-3 text-sm text-neutral-500 dark:text-neutral-400">
            {{ plan()?.teamName }}
            @if (plan()?.name) { <span>· {{ plan()?.name }}</span> }
          </p>
        </div>

        <!-- Saha: 2:3 oranlı dikey alan. İşaretler YÜZDE ile konumlanır, bu yüzden
             her ekran genişliğinde aynı yerde durur. -->
        <div class="relative mx-auto mt-10 w-full max-w-md overflow-hidden rounded-2xl border-2 border-white/40 shadow-lg"
             style="aspect-ratio:2/3;background:linear-gradient(180deg,#15803d 0%,#166534 100%)">

          <!-- Saha çizgileri: yalnız dekor, ekran okuyucudan gizli. -->
          <div aria-hidden="true" class="pointer-events-none absolute inset-0">
            <div class="absolute inset-x-[6%] inset-y-[4%] rounded-sm border-2 border-white/40"></div>
            <div class="absolute inset-x-[6%] top-1/2 border-t-2 border-white/40"></div>
            <div class="absolute left-1/2 top-1/2 h-[16%] w-[24%] -translate-x-1/2 -translate-y-1/2 rounded-full border-2 border-white/40"></div>
            <div class="absolute inset-x-[26%] top-[4%] h-[14%] border-2 border-t-0 border-white/40"></div>
            <div class="absolute inset-x-[26%] bottom-[4%] h-[14%] border-2 border-b-0 border-white/40"></div>
          </div>

          @for (n of nokta(); track $index) {
            <div class="absolute -translate-x-1/2 -translate-y-1/2 text-center"
                 [style.left.%]="n.x" [style.top.%]="n.y">
              <div class="mx-auto flex h-9 w-9 items-center justify-center rounded-full border-2 border-white bg-white/95 text-xs font-bold text-neutral-900 shadow sm:h-10 sm:w-10 sm:text-sm">
                {{ n.number || '' }}
              </div>
              @if (n.playerName) {
                <span class="mt-1 block whitespace-nowrap rounded bg-black/55 px-1.5 py-0.5 text-[10px] font-medium text-white sm:text-xs">{{ n.playerName }}</span>
              } @else if (n.label) {
                <span class="mt-1 block whitespace-nowrap rounded bg-black/40 px-1.5 py-0.5 text-[10px] font-medium text-white/90 sm:text-xs">{{ n.label }}</span>
              }
            </div>
          }
        </div>
      </div>
    </section>
    }
  `,
})
export class FormationPitchSection implements OnInit {
  protected text = inject(SiteChromeText);
  private svc = inject(TenantWebService);

  config = input<SiteConfig>({});
  clientId = input<number>(0);

  protected plan = signal<TenantFormation | null>(null);
  protected nokta = computed(() => this.plan()?.spots ?? []);

  ngOnInit() {
    const id = this.clientId();
    if (!id) return;

    const ham = cfg(this.config(), 'formationId', '');
    const planId = Number(ham) > 0 ? Number(ham) : null;

    this.svc.getSportsFormation(id, planId).subscribe({
      next: (p) => this.plan.set(p),
      error: () => this.plan.set(null),
    });
  }

  protected eyebrow = computed(() => cfg(this.config(), 'eyebrow', ''));
  protected title = computed(() => cfg(this.config(), 'title', this.text.t('fb.sahaPlani')));
}
